export class GitHubFetchError extends Error {
  code: 'not_found' | 'rate_limited' | 'no_skillmd' | 'invalid_url' | 'network';
  constructor(code: GitHubFetchError['code'], message: string) {
    super(message);
    this.code = code;
  }
}

export interface DirEntrySize { name: string; type: 'file' | 'dir'; size?: number }

export interface GitHubFetchResult {
  source: 'github';
  skillMd: string;
  directoryListing: string[];
  folderName: string;
  skillFilename: string | undefined;
  resolved: { owner: string; repo: string; branch: string; path: string };
  /** Recursive map of subfolder name to its file entries with sizes (one level deep). */
  subfolderEntries?: Record<string, DirEntrySize[]>;
}

interface ParsedUrl {
  owner: string;
  repo: string;
  branch?: string;
  path: string;
}

export function parseGitHubUrl(input: string): ParsedUrl {
  const trimmed = input.trim();
  let u: URL;
  try {
    u = new URL(trimmed);
  } catch {
    throw new GitHubFetchError('invalid_url', 'Not a valid URL.');
  }
  if (u.hostname !== 'github.com') {
    throw new GitHubFetchError('invalid_url', 'URL must be on github.com.');
  }
  const segments = u.pathname.replace(/^\/+/, '').replace(/\/+$/, '').split('/');
  if (segments.length < 2) {
    throw new GitHubFetchError('invalid_url', 'URL must include owner and repo.');
  }
  const [owner, repo, kind, branch, ...rest] = segments;
  if (!kind) return { owner, repo, path: '' };
  if (kind === 'tree') return { owner, repo, branch, path: rest.join('/') };
  if (kind === 'blob') {
    const filePath = rest.join('/');
    const parent = filePath.includes('/') ? filePath.slice(0, filePath.lastIndexOf('/')) : '';
    return { owner, repo, branch, path: parent };
  }
  return { owner, repo, path: '' };
}

async function jsonOrThrow(res: Response, ctx: string): Promise<unknown> {
  if (res.status === 404) {
    throw new GitHubFetchError('not_found', `Not found: ${ctx}`);
  }
  if (res.status === 403 && res.headers.get('X-RateLimit-Remaining') === '0') {
    throw new GitHubFetchError(
      'rate_limited',
      'GitHub rate-limited this IP. Try again later or paste the SKILL.md directly.',
    );
  }
  if (!res.ok) {
    throw new GitHubFetchError('network', `GitHub returned ${res.status} for ${ctx}.`);
  }
  return res.json();
}

export async function fetchFromGitHub(url: string): Promise<GitHubFetchResult> {
  const parsed = parseGitHubUrl(url);
  let branch = parsed.branch;
  if (!branch) {
    const res = await fetch(`https://api.github.com/repos/${parsed.owner}/${parsed.repo}`);
    const data = (await jsonOrThrow(res, 'repo metadata')) as { default_branch?: string };
    branch = data.default_branch ?? 'main';
  }

  const listingPath = parsed.path
    ? `/${encodeURIComponent(parsed.path).replace(/%2F/g, '/')}`
    : '';
  const listRes = await fetch(
    `https://api.github.com/repos/${parsed.owner}/${parsed.repo}/contents${listingPath}?ref=${encodeURIComponent(branch)}`,
  );
  const entries = (await jsonOrThrow(listRes, 'folder listing')) as Array<{
    name: string;
    type: 'file' | 'dir';
    size?: number;
  }>;

  if (!Array.isArray(entries)) {
    throw new GitHubFetchError('not_found', 'Expected a folder, got a single file.');
  }

  const directoryListing = entries.map((e) => (e.type === 'dir' ? `${e.name}/` : e.name));
  const skillFilename = entries.find(
    (e) => e.type === 'file' && e.name.toLowerCase() === 'skill.md',
  )?.name;

  let skillMd = '';
  if (skillFilename) {
    const rawPath = parsed.path ? `${parsed.path}/${skillFilename}` : skillFilename;
    const rawRes = await fetch(
      `https://raw.githubusercontent.com/${parsed.owner}/${parsed.repo}/${encodeURIComponent(branch)}/${rawPath
        .split('/')
        .map(encodeURIComponent)
        .join('/')}`,
    );
    if (!rawRes.ok) {
      throw new GitHubFetchError(
        'network',
        `Could not fetch SKILL.md (HTTP ${rawRes.status}).`,
      );
    }
    skillMd = await rawRes.text();
  }

  const folderName = parsed.path ? parsed.path.split('/').filter(Boolean).pop() ?? parsed.repo : parsed.repo;

  // Fetch one-level-deep listings for the conventional subfolders so we can
  // detect empty references/scripts/assets at runtime (progressive disclosure depth).
  const subfolderEntries: Record<string, DirEntrySize[]> = {};
  for (const sub of ['references', 'scripts', 'assets']) {
    if (!entries.some((e) => e.type === 'dir' && e.name === sub)) continue;
    const subPath = parsed.path ? `${parsed.path}/${sub}` : sub;
    try {
      const subRes = await fetch(
        `https://api.github.com/repos/${parsed.owner}/${parsed.repo}/contents/${encodeURIComponent(subPath).replace(/%2F/g, '/')}?ref=${encodeURIComponent(branch)}`,
      );
      if (subRes.ok) {
        const subEntries = (await subRes.json()) as DirEntrySize[];
        if (Array.isArray(subEntries)) subfolderEntries[sub] = subEntries;
      }
    } catch { /* tolerate per-subfolder failures */ }
  }

  return {
    source: 'github',
    skillMd,
    directoryListing,
    folderName,
    skillFilename,
    resolved: { owner: parsed.owner, repo: parsed.repo, branch, path: parsed.path },
    subfolderEntries,
  };
}
